import { createContext, useCallback } from 'react';
import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Flex, Box } from '..';
import Container from '../../modules/HowItWorks/Container';
import { ProgressIndicator } from './ProgressIndicator';
import { BackButton } from './BackButton';
import { usePrevious } from '../../lib/hooks';

export type State = {
  isLast: boolean;
  isFirst: boolean;
  finished: boolean;
};

function noop() {
  return;
}

const WizardContext = createContext<{
  state: State;
  goNext: () => void;
  goPrev: () => void;
}>({
  goNext: noop,
  goPrev: noop,
  state: {
    isLast: false,
    isFirst: false,
    finished: false
  }
});

const variants = {
  enter: ({
    direction,
    shouldReduceMotion
  }: {
    direction: number;
    shouldReduceMotion: boolean;
  }) => {
    let x;
    if (!shouldReduceMotion) {
      x = direction > 0 ? 1000 : -1000;
    }
    return {
      x,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: ({
    direction,
    shouldReduceMotion
  }: {
    direction: number;
    shouldReduceMotion: boolean;
  }) => {
    let x;
    if (!shouldReduceMotion) {
      x = direction < 0 ? 1000 : -1000;
    }
    return {
      x,
      zIndex: 0,
      opacity: 0
    };
  }
};

const numberWithinBounds = (min: number, max: number, num: number) =>
  Math.max(min, Math.min(max, num));

export function useWizardContext() {
  const context = React.useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizardState must be used within a Wizard');
  }
  return context;
}

export function Wizard({
  stepIndex,
  stepCount,
  onStepChange,
  children,
  showBackOnFirst = false
}: {
  stepIndex: number;
  stepCount: number;
  onStepChange: (props: State & { nextStepIndex: number }) => void;
  children: React.ReactElement;
  showBackOnFirst?: boolean;
}) {
  const prevStepIndex = usePrevious<number>(stepIndex);
  const shouldReduceMotion = useReducedMotion();

  const maxStepIndex = stepCount - 1;
  const direction = stepIndex > (prevStepIndex || -1) ? 1 : -1;

  const getState = useCallback(
    (stepIndex: number) => {
      const finished = stepIndex > maxStepIndex;
      const isLast = stepIndex === maxStepIndex;
      const isFirst = stepIndex === 0;

      return { finished, isLast, isFirst };
    },
    [stepIndex]
  );

  function goTo(nextStepIndex: number) {
    const state = getState(nextStepIndex);
    onStepChange({
      nextStepIndex: numberWithinBounds(0, maxStepIndex, nextStepIndex),
      ...state
    });
  }

  const goNext = useCallback(() => {
    goTo(stepIndex + 1);
  }, [stepIndex]);

  const goPrev = useCallback(() => {
    goTo(stepIndex - 1);
  }, [stepIndex]);

  const state = getState(stepIndex);
  const headerHeight = { _: '24px', lg: '32px' };

  return (
    <WizardContext.Provider value={{ state, goNext, goPrev }}>
      <Flex
        minHeight="100vh"
        position="relative"
        flexDirection="column"
        overflow="hidden"
      >
        <Flex
          justifyContent="center"
          alignItems="center"
          m="xs"
          position="relative"
          height={headerHeight}
        >
          <BackButton
            variant={!showBackOnFirst && state.isFirst ? 'hidden' : 'visible'}
            iconSize={headerHeight}
            onClick={() => {
              window.history.back();
            }}
          />
          <Box flex={1}>
            <ProgressIndicator
              numberOfSteps={stepCount}
              currentStepIndex={stepIndex}
            />
          </Box>
        </Flex>
        <Flex
          width="100vw"
          height="100vh"
          position="relative"
          alignItems="center"
          justifyContent="center"
          flex={1}
          mx="auto"
        >
          <AnimatePresence
            initial={false}
            custom={{ shouldReduceMotion, direction }}
            exitBeforeEnter={false}
          >
            <motion.div
              data-testid="WizardStep"
              key={stepIndex}
              custom={{ shouldReduceMotion, direction }}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              style={{
                position: 'absolute',
                maxWidth: '100vw'
              }}
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 40 },
                opacity: { duration: 0.2 }
              }}
            >
              <Container width={1} px="xs">
                {children}
              </Container>
            </motion.div>
          </AnimatePresence>
        </Flex>
      </Flex>
    </WizardContext.Provider>
  );
}
